/**
 * Load the user-uploaded models independently of R3F, bake the Y-up -> Z-up
 * transform (same as UploadedModel applies), and flatten everything into plain
 * typed arrays suitable for WebGPU storage buffers.
 *
 * v2 scope: scalar PBR material factors + baseColor and metallicRoughness
 * textures (canonicalized to a single per-array size so one `texture_2d_array`
 * can hold all layers). Normal maps are still deferred - they require a
 * tangent frame in the vertex data.
 *
 * Per-vertex data:
 *   positions: Float32Array (3 * vertexCount)
 *   normals:   Float32Array (3 * vertexCount)
 *   uvs:       Float32Array (4 * vertexCount)  // (u0,v0, u1,v1), zero-filled if missing
 *   indices:   Uint32Array  (3 * triCount)
 *   triMatIds: Uint32Array  (triCount)
 *
 * We carry BOTH UV channels (TEXCOORD_0 and TEXCOORD_1) because glTF textures
 * are allowed to reference either one per texture reference (`texCoord`
 * field). Using the wrong channel looks like the texture collapsed to a
 * single color (e.g. if uv0 is all-zero for a mesh whose actual texture
 * coords live in uv1). Each material records the channel its baseColor and
 * metallicRoughness textures want, and the shader picks at sample time.
 *
 * Each textured material carries two layer indices (`baseColorLayer`,
 * `mrLayer`). -1 means "no texture for that channel"; the shader falls
 * back to the scalar factor in that case.
 *
 * Textures are returned as `ImageBitmap`s (decoded straight from the GLB)
 * normalized to a shared canonical size, so the engine can upload them via
 * `device.queue.copyExternalImageToTexture` - the native WebGPU path that
 * avoids a canvas `getImageData` round-trip (which would be CORS-sensitive
 * and error-prone in practice).
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';

interface UploadedModelRef {
  name: string;
  url: string;
}

export interface SceneMaterial {
  baseColor: [number, number, number];
  metallic: number;
  roughness: number;
  emissive: [number, number, number];
  baseColorLayer: number;
  mrLayer: number;
  /**
   * Per-channel 2D UV transform derived from `THREE.Texture.matrix` (which
   * bakes repeat/offset/rotation/center, including the KHR_texture_transform
   * extension when present). Stored as two rows of an affine 3x3 so the
   * shader can compute `u' = row0.x*u + row0.y*v + row0.z`,
   *                   `v' = row1.x*u + row1.y*v + row1.z`.
   * Identity when the texture has no custom transform.
   */
  baseColorUvRow0: [number, number, number];
  baseColorUvRow1: [number, number, number];
  mrUvRow0: [number, number, number];
  mrUvRow1: [number, number, number];
  /**
   * Which UV set this texture samples - 0 for `attributes.uv` (TEXCOORD_0) or
   * 1 for `attributes.uv1` (TEXCOORD_1). Sourced from `THREE.Texture.channel`,
   * which GLTFLoader sets from the glTF texture-info `texCoord` field. Meshes
   * commonly carry both UV sets with only one of them actually meaningful for
   * a given texture, so picking the wrong channel is what produces the classic
   * "texture collapses to a single color" symptom.
   */
  baseColorUvChannel: number;
  mrUvChannel: number;
}

interface TextureLayer {
  /**
   * Full mipmap chain, `bitmaps[0]` is the canonical-size mip and each
   * subsequent entry halves each dimension down to 1x1. Generated on the CPU
   * via progressive canvas downsamples (browser-native high-quality
   * imageSmoothing). Uploaded as individual mip levels on the GPU so the
   * sampler can pick an appropriate level per hit and avoid aliasing on
   * high-frequency textures.
   */
  bitmaps: ImageBitmap[];
  width: number;
  height: number;
}

export interface ExtractedScene {
  positions: Float32Array;
  normals: Float32Array;
  uvs: Float32Array;
  indices: Uint32Array;
  triMaterialIds: Uint32Array;
  materials: SceneMaterial[];
  baseColorTextures: TextureLayer[];
  metallicRoughnessTextures: TextureLayer[];
  /** Canonical per-layer size all texture arrays share. */
  textureSize: number;
  bounds: { min: [number, number, number]; max: [number, number, number] };
}

const Y_UP_TO_Z_UP = new THREE.Matrix4().set(1, 0, 0, 0, 0, 0, -1, 0, 0, 1, 0, 0, 0, 0, 0, 1);

const IDENTITY_ROW0: [number, number, number] = [1, 0, 0];
const IDENTITY_ROW1: [number, number, number] = [0, 1, 0];

const DEFAULT_MATERIAL: SceneMaterial = {
  baseColor: [0.72, 0.72, 0.72],
  metallic: 0.0,
  roughness: 0.6,
  emissive: [0, 0, 0],
  baseColorLayer: -1,
  mrLayer: -1,
  baseColorUvRow0: [...IDENTITY_ROW0],
  baseColorUvRow1: [...IDENTITY_ROW1],
  mrUvRow0: [...IDENTITY_ROW0],
  mrUvRow1: [...IDENTITY_ROW1],
  baseColorUvChannel: 0,
  mrUvChannel: 0,
};

/** Canonical size for rasterized material textures. 1024 preserves most
 *  arch-viz detail while keeping total memory manageable (4 MB per layer). */
const CANONICAL_TEXTURE_SIZE = 1024;

async function loadObject(model: UploadedModelRef): Promise<{ root: THREE.Object3D; ext: string }> {
  const name = model.name.toLowerCase();
  const urlPath = model.url.split('?')[0].toLowerCase();
  const ext =
    (name.includes('.') ? name.split('.').pop() : undefined) ||
    (urlPath.includes('.') ? urlPath.split('.').pop() : undefined) ||
    '';
  if (ext === 'glb' || ext === 'gltf') {
    const gltf = await new GLTFLoader().loadAsync(model.url);
    return { root: gltf.scene, ext };
  }
  if (ext === 'obj') {
    return { root: await new OBJLoader().loadAsync(model.url), ext };
  }
  throw new Error(`Unsupported model format: ${ext} (${model.name})`);
}

interface SourceDescriptor {
  /** A form `createImageBitmap` can accept (or an already-decoded bitmap). */
  source: ImageBitmapSource;
  /** Short name of the runtime type we extracted, for logging. */
  kind: string;
  /** Width reported by the source, before we normalize to an ImageBitmap. */
  reportedW: number;
  /** Height reported by the source, before we normalize to an ImageBitmap. */
  reportedH: number;
}

/**
 * Pull the image source out of a THREE.Texture, handling every shape three.js
 * may hand us. This is deliberately broad: we'd rather attempt to decode a
 * weird source (and log the result) than silently drop it.
 */
function getTextureImageSource(texture: THREE.Texture): SourceDescriptor | null {
  const src = texture.source as { data?: unknown } | undefined;
  const fromSource = src && typeof src === 'object' ? src.data : undefined;
  const fromImage = (texture as unknown as { image?: unknown }).image;
  const candidate = fromSource ?? fromImage;
  if (!candidate || typeof candidate !== 'object') return null;

  const desc = (source: ImageBitmapSource, kind: string, w: number, h: number): SourceDescriptor => ({
    source,
    kind,
    reportedW: w,
    reportedH: h,
  });

  if (typeof HTMLImageElement !== 'undefined' && candidate instanceof HTMLImageElement) {
    return desc(candidate, 'HTMLImageElement', candidate.naturalWidth, candidate.naturalHeight);
  }
  if (typeof HTMLCanvasElement !== 'undefined' && candidate instanceof HTMLCanvasElement) {
    return desc(candidate, 'HTMLCanvasElement', candidate.width, candidate.height);
  }
  if (typeof OffscreenCanvas !== 'undefined' && candidate instanceof OffscreenCanvas) {
    return desc(candidate, 'OffscreenCanvas', candidate.width, candidate.height);
  }
  if (typeof ImageBitmap !== 'undefined' && candidate instanceof ImageBitmap) {
    return desc(candidate, 'ImageBitmap', candidate.width, candidate.height);
  }
  if (typeof ImageData !== 'undefined' && candidate instanceof ImageData) {
    return desc(candidate, 'ImageData', candidate.width, candidate.height);
  }
  // Plain { data: TypedArray, width, height } (compressed-texture placeholders,
  // KTX2/Basis mipmap entries, etc.). We can only handle it if `data` looks
  // like a flat 8-bit RGBA buffer; anything exotic we give up on here rather
  // than guess and produce a silently-wrong texture.
  const loose = candidate as {
    data?: ArrayBufferView;
    width?: number;
    height?: number;
    mipmaps?: unknown;
    format?: unknown;
  };
  if (
    loose.data instanceof Uint8Array &&
    typeof loose.width === 'number' &&
    typeof loose.height === 'number' &&
    loose.width > 0 &&
    loose.height > 0 &&
    loose.data.length === loose.width * loose.height * 4
  ) {
    // Copy the view into its own fresh buffer so we pass the strict
    // `Uint8ClampedArray<ArrayBuffer>` variant ImageData demands (the source
    // may be a SharedArrayBuffer view, which the constructor rejects).
    const copy = new Uint8ClampedArray(loose.data.length);
    copy.set(new Uint8Array(loose.data.buffer, loose.data.byteOffset, loose.data.byteLength));
    const imageData = new ImageData(copy, loose.width, loose.height);
    return desc(imageData, 'RawRGBA8', loose.width, loose.height);
  }
  return null;
}

function makeCanvas(w: number, h: number): OffscreenCanvas | HTMLCanvasElement {
  if (typeof OffscreenCanvas !== 'undefined') {
    return new OffscreenCanvas(w, h);
  }
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

function get2dCtx(
  canvas: OffscreenCanvas | HTMLCanvasElement,
): OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null {
  return canvas.getContext('2d') as OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null;
}

/**
 * Sample a small strided patch of an already-drawn canvas and return a crude
 * "is this mostly empty or flat?" flag together with the observed range so we
 * can log it alongside the texture. Intentionally lightweight - we just need
 * enough signal to flag "this mip 0 came out as solid black / solid color",
 * which is the main silent-failure mode of the extraction path.
 *
 * Returns null if reading pixels throws (tainted canvas, etc.).
 */
function sampleMip0(
  ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D,
  size: number,
): {
  suspicious: boolean;
  reason: string;
  range: [[number, number], [number, number], [number, number]];
  alphaAvg: number;
} | null {
  try {
    // Read a grid of samples across the whole mip so we catch "top-left
    // quadrant looks fine, rest is black" failures too.
    const patch = Math.min(32, size);
    const stride = Math.max(1, Math.floor((size - patch) / 3));
    let minR = 255,
      maxR = 0,
      minG = 255,
      maxG = 0,
      minB = 255,
      maxB = 0,
      sumA = 0,
      nA = 0;
    for (let py = 0; py <= size - patch; py += stride) {
      for (let px = 0; px <= size - patch; px += stride) {
        const img = ctx.getImageData(px, py, patch, patch);
        const d = img.data;
        for (let i = 0; i < d.length; i += 4) {
          const r = d[i];
          const g = d[i + 1];
          const b = d[i + 2];
          if (r < minR) minR = r;
          if (r > maxR) maxR = r;
          if (g < minG) minG = g;
          if (g > maxG) maxG = g;
          if (b < minB) minB = b;
          if (b > maxB) maxB = b;
          sumA += d[i + 3];
          nA++;
        }
      }
    }
    const alphaAvg = nA > 0 ? sumA / (nA * 255) : 0;
    // A texture that's all-alpha-zero is obviously broken (transparent
    // everything). A texture whose RGB range is <=1 across the patches is
    // essentially one color - could be legit (solid-color lookup) but usually
    // indicates the source wasn't drawn.
    let suspicious = false;
    let reason = '';
    if (alphaAvg < 0.05) {
      suspicious = true;
      reason = 'mostly transparent';
    } else if (maxR - minR <= 1 && maxG - minG <= 1 && maxB - minB <= 1) {
      suspicious = true;
      reason = 'flat color';
    }
    return {
      suspicious,
      reason,
      range: [
        [minR, maxR],
        [minG, maxG],
        [minB, maxB],
      ],
      alphaAvg,
    };
  } catch {
    return null;
  }
}

/**
 * Turn an arbitrarily-sized image source into a full mip chain of ImageBitmaps.
 *
 * Sources are first normalized to an ImageBitmap via `createImageBitmap`. That
 * step is important for three reasons:
 *   - It awaits the source's decode (HTMLImageElement can reach us before
 *     `.naturalWidth` is valid or before the pixels are actually decoded).
 *   - It handles every source kind uniformly (ImageData, Canvas, raw bitmap).
 *   - It produces a fresh bitmap we own, so upstream lifetimes (e.g. three.js
 *     closing an autoClose bitmap after WebGL upload) don't matter.
 *
 * Sources are then stretched to fit the canonical square; because mesh UVs are
 * normalized, sampling at mesh-UV (u,v) still retrieves source-UV (u,v)
 * exactly, so the surface result is indistinguishable from sampling the
 * original texture directly. The advantage is that hardware repeat wrap works
 * natively for tiled UVs (no per-material uv-scale gymnastics that only work
 * when the source size is an integer divisor of the canonical size).
 *
 * Lower mip levels are produced by progressively downsampling the previous
 * mip through `drawImage` with `imageSmoothingQuality = 'high'`. That's a
 * box/bilinear filter, good enough to kill high-frequency aliasing from very
 * tiled UVs (floors, fabrics, etc.), and the browser's image resampler is
 * usually hardware-accelerated.
 *
 * `flipY` is baked into mip 0 so the sampler always sees V=0 at the top.
 */
async function canonicalizeToBitmaps(
  desc: SourceDescriptor,
  canonicalSize: number,
  flipY: boolean,
  debugTag: string,
): Promise<{ bitmaps: ImageBitmap[]; srcW: number; srcH: number } | null> {
  let decoded: ImageBitmap;
  try {
    decoded = await createImageBitmap(desc.source);
  } catch (err) {
    console.warn('[pathtracer] createImageBitmap failed', debugTag, {
      kind: desc.kind,
      reportedW: desc.reportedW,
      reportedH: desc.reportedH,
      err,
    });
    return null;
  }

  try {
    const srcW = decoded.width;
    const srcH = decoded.height;
    if (!srcW || !srcH) {
      console.warn('[pathtracer] decoded bitmap has zero dimension', debugTag, {
        kind: desc.kind,
      });
      decoded.close();
      return null;
    }

    const mip0Canvas = makeCanvas(canonicalSize, canonicalSize);
    const mip0Ctx = get2dCtx(mip0Canvas);
    if (!mip0Ctx) {
      decoded.close();
      return null;
    }
    mip0Ctx.imageSmoothingEnabled = true;
    mip0Ctx.imageSmoothingQuality = 'high';
    if (flipY) {
      mip0Ctx.translate(0, canonicalSize);
      mip0Ctx.scale(1, -1);
    }
    mip0Ctx.drawImage(decoded, 0, 0, canonicalSize, canonicalSize);
    // drawImage consumed the bitmap; reset the transform so the sanity sampler
    // doesn't see a flipped coordinate system.
    if (flipY) mip0Ctx.setTransform(1, 0, 0, 1, 0, 0);

    const sample = sampleMip0(mip0Ctx, canonicalSize);
    if (sample && sample.suspicious) {
      console.warn('[pathtracer] mip 0 looks empty/flat after drawImage', debugTag, {
        kind: desc.kind,
        reportedW: desc.reportedW,
        reportedH: desc.reportedH,
        decodedW: srcW,
        decodedH: srcH,
        reason: sample.reason,
        alphaAvg: sample.alphaAvg,
        rRange: sample.range[0],
        gRange: sample.range[1],
        bRange: sample.range[2],
      });
    }

    const bitmaps: ImageBitmap[] = [];
    bitmaps.push(await createImageBitmap(mip0Canvas as ImageBitmapSource));

    let prevCanvas: OffscreenCanvas | HTMLCanvasElement = mip0Canvas;
    let prevSize = canonicalSize;
    while (prevSize > 1) {
      const nextSize = Math.max(1, prevSize >> 1);
      const nextCanvas = makeCanvas(nextSize, nextSize);
      const nextCtx = get2dCtx(nextCanvas);
      if (!nextCtx) break;
      nextCtx.imageSmoothingEnabled = true;
      nextCtx.imageSmoothingQuality = 'high';
      nextCtx.drawImage(prevCanvas as CanvasImageSource, 0, 0, nextSize, nextSize);
      bitmaps.push(await createImageBitmap(nextCanvas as ImageBitmapSource));
      prevCanvas = nextCanvas;
      prevSize = nextSize;
    }

    decoded.close();
    return {
      bitmaps,
      srcW,
      srcH,
    };
  } catch (err) {
    console.warn('[pathtracer] failed to canonicalize texture source:', debugTag, err);
    decoded.close();
    return null;
  }
}

async function extractTextureLayer(
  texture: THREE.Texture,
  channel: 'baseColor' | 'mr',
  size: number,
): Promise<{ layer: TextureLayer; srcW: number; srcH: number } | null> {
  const desc = getTextureImageSource(texture);
  // Short tag to correlate the per-texture warn messages with the summary
  // log; "first-8-of-uuid:channel" is unique enough in practice.
  const tag = `${texture.uuid.slice(0, 8)}:${channel}`;
  if (!desc) {
    console.warn('[pathtracer] material texture has no usable image source', tag, {
      name: texture.name,
      uuid: texture.uuid,
      sourceDataType: typeof (texture.source as { data?: unknown } | undefined)?.data,
      hasImage: (texture as unknown as { image?: unknown }).image !== undefined,
    });
    return null;
  }
  const result = await canonicalizeToBitmaps(desc, size, texture.flipY, tag);
  if (!result) return null;
  return {
    layer: {
      bitmaps: result.bitmaps,
      width: size,
      height: size,
    },
    srcW: result.srcW,
    srcH: result.srcH,
  };
}

/**
 * Extract the 2D affine UV transform baked into `texture.matrix`. Three.js
 * composes `repeat`, `offset`, `rotation`, `center` (and `KHR_texture_transform`
 * for glTF) into this Matrix3, so using it directly means we get all of them
 * for free. `texture.matrixAutoUpdate` is true by default; we still call
 * `updateMatrix()` defensively in case something downstream mutated the fields
 * without letting three.js rebuild the matrix.
 */
function readTextureUvTransform(texture: THREE.Texture): {
  row0: [number, number, number];
  row1: [number, number, number];
} {
  if (texture.matrixAutoUpdate) texture.updateMatrix();
  const e = texture.matrix.elements;
  // three.js Matrix3 is column-major:
  //   [e0 e3 e6]   [m11 m12 m13]
  //   [e1 e4 e7] = [m21 m22 m23]
  //   [e2 e5 e8]   [m31 m32 m33]
  return {
    row0: [e[0], e[3], e[6]],
    row1: [e[1], e[4], e[7]],
  };
}

function resolveScalars(mat: THREE.Material | null | undefined): SceneMaterial {
  const out: SceneMaterial = { ...DEFAULT_MATERIAL };
  if (!mat) return out;
  if ('color' in mat && (mat as { color: THREE.Color }).color) {
    const c = (mat as { color: THREE.Color }).color;
    out.baseColor = [c.r, c.g, c.b];
  }
  if ('metalness' in mat) {
    out.metallic = (mat as THREE.MeshStandardMaterial).metalness ?? 0;
  }
  if ('roughness' in mat) {
    const r = (mat as THREE.MeshStandardMaterial).roughness;
    out.roughness = r != null ? Math.max(0.04, r) : 0.6;
  }
  if ('emissive' in mat) {
    const e = (mat as THREE.MeshStandardMaterial).emissive;
    const intensity = (mat as THREE.MeshStandardMaterial).emissiveIntensity ?? 1;
    if (e) out.emissive = [e.r * intensity, e.g * intensity, e.b * intensity];
  }
  return out;
}

function getMaterialTextures(mat: THREE.Material | null | undefined): {
  baseColor: THREE.Texture | null;
  mr: THREE.Texture | null;
} {
  const std = mat as THREE.MeshStandardMaterial | null;
  return {
    baseColor: std?.map ?? null,
    // glTF packs metal/rough into a single texture; three.js exposes it on
    // both metalnessMap and roughnessMap (same instance for glTF assets).
    mr: std?.metalnessMap ?? std?.roughnessMap ?? null,
  };
}

export async function extractScene(models: UploadedModelRef[]): Promise<ExtractedScene> {
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const triMaterialIds: number[] = [];
  const materials: SceneMaterial[] = [];
  const materialKeyToIndex = new Map<string, number>();

  // Collected texture work; we kick off extraction in parallel at the end
  // to overlap multiple image decodes. Each entry records where its final
  // layer index should be written back into `materials`.
  type TexJob = {
    texture: THREE.Texture;
    targetMaterialIndex: number;
    channel: 'baseColor' | 'mr';
  };
  const texJobs: TexJob[] = [];

  const boundsMin: [number, number, number] = [Infinity, Infinity, Infinity];
  const boundsMax: [number, number, number] = [-Infinity, -Infinity, -Infinity];

  function getOrAddMaterial(mat: THREE.Material | null | undefined): number {
    const key = mat?.uuid ?? '__default__';
    const cached = materialKeyToIndex.get(key);
    if (cached !== undefined) return cached;
    const matIndex = materials.length;
    materialKeyToIndex.set(key, matIndex);
    const record = resolveScalars(mat);
    materials.push(record);
    const tex = getMaterialTextures(mat);
    if (tex.baseColor) {
      texJobs.push({ texture: tex.baseColor, targetMaterialIndex: matIndex, channel: 'baseColor' });
    }
    if (tex.mr) {
      texJobs.push({ texture: tex.mr, targetMaterialIndex: matIndex, channel: 'mr' });
    }
    return matIndex;
  }

  for (const model of models) {
    const { root, ext } = await loadObject(model);

    // Match UploadedModel.tsx behavior: GLB/GLTF are authored Y-up and get
    // swapped into Z-up on the fly; OBJ files are already authored Z-up.
    if (ext === 'glb' || ext === 'gltf') {
      root.applyMatrix4(Y_UP_TO_Z_UP);
    }
    root.updateMatrixWorld(true);

    const meshes: THREE.Mesh[] = [];
    root.traverse((obj) => {
      if ((obj as THREE.Mesh).isMesh) meshes.push(obj as THREE.Mesh);
    });

    for (const mesh of meshes) {
      mesh.updateMatrixWorld(true);
      const geometry = (mesh.geometry as THREE.BufferGeometry).clone();
      geometry.applyMatrix4(mesh.matrixWorld);
      if (!geometry.attributes.position) continue;
      if (!geometry.attributes.normal) geometry.computeVertexNormals();

      const posAttr = geometry.attributes.position;
      const normAttr = geometry.attributes.normal;
      // TEXCOORD_0 and TEXCOORD_1. three.js r151+ exposes the second set as
      // `uv1`; earlier versions used `uv2`, so check both defensively. If a
      // mesh has only the primary set we fall it through to uv1 so textures
      // authored for TEXCOORD_1 still render somewhere plausible instead of
      // all-zero.
      const uv0Attr = geometry.attributes.uv;
      const uv1Attr =
        (geometry.attributes.uv1 as THREE.BufferAttribute | undefined) ??
        (geometry.attributes as { uv2?: THREE.BufferAttribute }).uv2;
      const geomIndex = geometry.index ? geometry.index.array : null;

      const matCandidate = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
      const matId = getOrAddMaterial(matCandidate);

      const baseVertex = positions.length / 3;
      for (let i = 0; i < posAttr.count; i++) {
        const x = posAttr.getX(i);
        const y = posAttr.getY(i);
        const z = posAttr.getZ(i);
        positions.push(x, y, z);
        if (x < boundsMin[0]) boundsMin[0] = x;
        if (y < boundsMin[1]) boundsMin[1] = y;
        if (z < boundsMin[2]) boundsMin[2] = z;
        if (x > boundsMax[0]) boundsMax[0] = x;
        if (y > boundsMax[1]) boundsMax[1] = y;
        if (z > boundsMax[2]) boundsMax[2] = z;
        normals.push(normAttr.getX(i), normAttr.getY(i), normAttr.getZ(i));
        const u0 = uv0Attr ? uv0Attr.getX(i) : 0;
        const v0 = uv0Attr ? uv0Attr.getY(i) : 0;
        // Fallback chain: explicit uv1 > uv0 > zero. Mirrors what GLTFLoader
        // does when a texCoord slot is unbound, and keeps us from ever
        // silently sampling all (0,0) because one UV set was missing.
        const u1 = uv1Attr ? uv1Attr.getX(i) : u0;
        const v1 = uv1Attr ? uv1Attr.getY(i) : v0;
        uvs.push(u0, v0, u1, v1);
      }

      if (geomIndex) {
        for (let i = 0; i < geomIndex.length; i += 3) {
          indices.push(geomIndex[i] + baseVertex, geomIndex[i + 1] + baseVertex, geomIndex[i + 2] + baseVertex);
          triMaterialIds.push(matId);
        }
      } else {
        for (let i = 0; i < posAttr.count; i += 3) {
          indices.push(baseVertex + i, baseVertex + i + 1, baseVertex + i + 2);
          triMaterialIds.push(matId);
        }
      }

      geometry.dispose();
    }
  }

  if (!isFinite(boundsMin[0])) {
    boundsMin[0] = boundsMin[1] = boundsMin[2] = 0;
    boundsMax[0] = boundsMax[1] = boundsMax[2] = 0;
  }

  if (materials.length === 0) materials.push({ ...DEFAULT_MATERIAL });

  const baseColorTextures: TextureLayer[] = [];
  const mrTextures: TextureLayer[] = [];
  type TexResolved = {
    layer: number;
    srcW: number;
    srcH: number;
    row0: [number, number, number];
    row1: [number, number, number];
    channel: number;
  };
  const baseColorByUuid = new Map<string, TexResolved>();
  const mrByUuid = new Map<string, TexResolved>();

  type UniqueJob = {
    texture: THREE.Texture;
    channel: 'baseColor' | 'mr';
    promise: Promise<{ layer: TextureLayer; srcW: number; srcH: number } | null>;
  };
  const uniqueJobs = new Map<string, UniqueJob>();
  for (const job of texJobs) {
    const key = `${job.channel}:${job.texture.uuid}`;
    if (!uniqueJobs.has(key)) {
      uniqueJobs.set(key, {
        texture: job.texture,
        channel: job.channel,
        promise: extractTextureLayer(job.texture, job.channel, CANONICAL_TEXTURE_SIZE),
      });
    }
  }

  for (const [, entry] of uniqueJobs) {
    const result = await entry.promise;
    if (!result) continue;
    const transform = readTextureUvTransform(entry.texture);
    // `texture.channel` picks TEXCOORD_0 vs TEXCOORD_1; three.js >= r151 sets
    // it from the glTF texture-info `texCoord` field (KHR_texture_transform
    // may override it further). We only have uv0/uv1 in the vertex buffer,
    // so anything beyond channel 1 is clamped to 0 and logged as unsupported.
    const rawChannel = (entry.texture as { channel?: number }).channel ?? 0;
    const channel = rawChannel === 0 || rawChannel === 1 ? rawChannel : 0;
    if (rawChannel !== channel) {
      console.warn('[pathtracer] texture uses unsupported UV channel; falling back to 0', {
        uuid: entry.texture.uuid,
        channel: rawChannel,
      });
    }
    const pack: TexResolved = {
      layer: -1,
      srcW: result.srcW,
      srcH: result.srcH,
      row0: transform.row0,
      row1: transform.row1,
      channel,
    };
    if (entry.channel === 'baseColor') {
      pack.layer = baseColorTextures.length;
      baseColorTextures.push(result.layer);
      baseColorByUuid.set(entry.texture.uuid, pack);
    } else {
      pack.layer = mrTextures.length;
      mrTextures.push(result.layer);
      mrByUuid.set(entry.texture.uuid, pack);
    }
  }

  for (const job of texJobs) {
    const mat = materials[job.targetMaterialIndex];
    const resolved =
      job.channel === 'baseColor' ? baseColorByUuid.get(job.texture.uuid) : mrByUuid.get(job.texture.uuid);
    if (!resolved) continue;
    if (job.channel === 'baseColor') {
      mat.baseColorLayer = resolved.layer;
      mat.baseColorUvRow0 = resolved.row0;
      mat.baseColorUvRow1 = resolved.row1;
      mat.baseColorUvChannel = resolved.channel;
    } else {
      mat.mrLayer = resolved.layer;
      mat.mrUvRow0 = resolved.row0;
      mat.mrUvRow1 = resolved.row1;
      mat.mrUvChannel = resolved.channel;
    }
  }

  const texturedCount = materials.filter((m) => m.baseColorLayer >= 0 || m.mrLayer >= 0).length;
  // Log a short summary plus the first few textures' raw dimensions and UV
  // transform so we can see at a glance whether glTF KHR_texture_transform is
  // contributing a non-identity matrix (those are often the cause of "textures
  // scaled wrong" issues).
  const sampleTransforms: Array<{
    channel: string;
    uvSet: number;
    uuid: string;
    srcW: number;
    srcH: number;
    row0: [number, number, number];
    row1: [number, number, number];
  }> = [];
  for (const [, resolved] of baseColorByUuid) {
    if (sampleTransforms.length >= 4) break;
    sampleTransforms.push({
      channel: 'baseColor',
      uvSet: resolved.channel,
      uuid: '…',
      srcW: resolved.srcW,
      srcH: resolved.srcH,
      row0: resolved.row0,
      row1: resolved.row1,
    });
  }
  for (const [, resolved] of mrByUuid) {
    if (sampleTransforms.length >= 8) break;
    sampleTransforms.push({
      channel: 'mr',
      uvSet: resolved.channel,
      uuid: '…',
      srcW: resolved.srcW,
      srcH: resolved.srcH,
      row0: resolved.row0,
      row1: resolved.row1,
    });
  }
  console.info('[pathtracer] scene extracted', {
    meshes: models.length,
    triangles: indices.length / 3,
    materials: materials.length,
    baseColorLayers: baseColorTextures.length,
    mrLayers: mrTextures.length,
    texturedMaterials: texturedCount,
    sampleTransforms,
  });

  return {
    positions: new Float32Array(positions),
    normals: new Float32Array(normals),
    uvs: new Float32Array(uvs),
    indices: new Uint32Array(indices),
    triMaterialIds: new Uint32Array(triMaterialIds),
    materials,
    baseColorTextures,
    metallicRoughnessTextures: mrTextures,
    textureSize: CANONICAL_TEXTURE_SIZE,
    bounds: { min: boundsMin, max: boundsMax },
  };
}
